/**
 * Shared helpers for GA4-style Data Layer context and section values
 * (see Analytics Implementation Strategy PDFs).
 */

const normalizeString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
};

const CONTEXT_TITLE_SELECTOR = [
  '[data-dl-title]',
  '[class*="__title"]',
  '[class*="__heading"]',
  'h1',
  'h2',
  'h3',
].join(',');

const getContextFromNearestTitle = (element) => {
  let root = element?.parentElement || null;

  while (root && root !== document.body) {
    const title = root.querySelector(CONTEXT_TITLE_SELECTOR);
    const titleText = normalizeString(title?.textContent);

    if (titleText && !title.contains(element)) {
      return titleText;
    }

    root = root.parentElement;
  }

  return '';
};

/**
 * Map current document to a stable "section" / page-type label for analytics.
 * @returns {string}
 */
export const getAnalyticsSection = () => {
  if (typeof document === 'undefined' || !document.body) {
    return '';
  }

  const bodyClasses = document.body.className.split(/\s+/);

  if (bodyClasses.includes('post-type-archive-product')) {
    return 'Product Listing';
  }

  if (bodyClasses.includes('single-product')) {
    return 'Decanter Details';
  }

  const postTypeClass = bodyClasses.find((cls) => cls.startsWith('post-type-'));
  if (postTypeClass) {
    const postType = postTypeClass.replace('post-type-', '');
    const sectionMap = {
      'digital-cellar': 'Cellar',
      dashboard: 'Dashboard',
      'my-profile': 'My Profile',
      concierge: 'Concierge',
    };
    if (sectionMap[postType]) {
      return sectionMap[postType];
    }
  }

  if (bodyClasses.includes('front-page') || bodyClasses.includes('home')) {
    return 'Homepage';
  }

  const pageClass = bodyClasses.find((cls) => cls.includes('cellar'));
  if (pageClass) {
    return 'Cellar';
  }

  const dashboardClass = bodyClasses.find((cls) => cls.includes('dashboard'));
  if (dashboardClass) {
    return 'Dashboard';
  }

  const profileClass = bodyClasses.find((cls) => cls.includes('profile'));
  if (profileClass) {
    return 'My Profile';
  }

  const conciergeClass = bodyClasses.find((cls) => cls.includes('concierge'));
  if (conciergeClass) {
    return 'Concierge';
  }

  return document.title || '';
};

/**
 * Best-effort "context" (area title) for an element, per analytics spec.
 * Prefer explicit `data-dl-context` on an ancestor when markup supports it.
 * @param {Element|null} element
 * @returns {string}
 */
export const getAnalyticsContextFromElement = (element) => {
  if (!element || typeof element.closest !== 'function') {
    return '';
  }

  const explicit = element.closest('[data-dl-context]');
  if (explicit) {
    return normalizeString(explicit.getAttribute('data-dl-context'));
  }

  const cellarSection = element.closest('.digital-cellar__section');
  if (cellarSection) {
    const title = cellarSection.querySelector('.digital-cellar__section-title');
    if (title) {
      return normalizeString(title.textContent);
    }
  }

  const article = element.closest('.decanter-detail__article');
  if (article) {
    const descHeading = article.querySelector(
      '.decanter-detail__description h2, .decanter-detail__description h3',
    );
    if (descHeading) {
      return normalizeString(descHeading.textContent);
    }
  }

  const heroWrapper = element.closest('.hero-landing__wrapper');
  if (heroWrapper) {
    const heroTitle = heroWrapper.querySelector('.hero-landing__title');
    if (heroTitle) {
      return normalizeString(heroTitle.textContent);
    }
  }

  const mediaBasic = element.closest('.media-basic-text');
  if (mediaBasic) {
    const blockTitle = mediaBasic.querySelector('.media-basic-text__title');
    if (blockTitle) {
      return normalizeString(blockTitle.textContent);
    }
  }

  return getContextFromNearestTitle(element);
};

/**
 * Product-level fields when the user is on a decanter / product detail page.
 * @returns {{ name: string, id: string, collectionName: string }}
 */
export const getDecanterPageProductMeta = () => {
  const article = document.querySelector('.decanter-detail__article');
  if (!article) {
    return { name: '', id: '', collectionName: '' };
  }

  const name = normalizeString(document.querySelector('.decanter-detail__title')?.textContent);
  const collectionName = normalizeString(
    document.querySelector('.decanter-detail__collection-name')?.textContent,
  );

  const wallet = document.querySelector('#product-wallet');
  let id = normalizeString(wallet?.dataset?.serial);

  if (!id) {
    const postMatch = article.id && article.id.match(/^post-(\d+)$/);
    if (postMatch) {
      const [, postId] = postMatch;
      id = postId;
    }
  }

  return {
    name,
    id,
    collectionName,
  };
};

/**
 * Item-level fields attached to CTA markup or the current product detail page.
 * @param {Element|null} element
 * @returns {{ name: string, id: string, collectionName: string }}
 */
export const getAnalyticsItemMetaFromElement = (element) => {
  const metaRoot = element?.closest(
    '[data-decanter-name], [data-decanter-id], [data-collection-name]',
  );
  const productMeta = getDecanterPageProductMeta();
  const decanterName = metaRoot?.dataset?.decanterName || metaRoot?.dataset?.name;
  const decanterId = metaRoot?.dataset?.decanterId || metaRoot?.dataset?.id;

  return {
    name: normalizeString(decanterName || productMeta.name),
    id: normalizeString(decanterId || productMeta.id),
    collectionName: normalizeString(
      metaRoot?.dataset?.collectionName || productMeta.collectionName,
    ),
  };
};
